import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getToken } from '../utils/auth';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as backendRefreshToken } from '../utils/refreshToken';
import { api } from '../api/client';
import { formDraftService } from '../services/FormDraftService';

interface SessionManagerOptions {
  warningTime?: number; // seconds before timeout to show warning
  timeoutDuration?: number; // total session timeout in seconds
  autoSaveInterval?: number; // auto-save interval in seconds
}

export const useSessionManager = (options: SessionManagerOptions = {}) => {
  const {
    warningTime = 60, // 1 minute warning
    timeoutDuration = 1800, // 30 minutes total
    autoSaveInterval = 30 // auto-save every 30 seconds
  } = options;

  const { logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timeoutDuration);
  const [isActive, setIsActive] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFormDataRef = useRef<any>(null);
  const lastStepRef = useRef<number>(0);
  const lastResetTime = useRef<number>(0);
  const lastRefreshAttempt = useRef<number>(0);
  const proactiveRefreshTimeoutRef = useRef<number | null>(null);
  const lastActivityLogTime = useRef<number>(0);

  // Reset session on user activity - throttled to prevent excessive calls
  const resetSession = useCallback(() => {
    const now = Date.now();
    // Throttle resets to prevent excessive calls (max once per 5 seconds)
    if (now - lastResetTime.current < 5000) {
      return;
    }
    
    lastResetTime.current = now;

    setTimeLeft(timeoutDuration);
    setLastActivity(now);
    setIsActive(true);
    setShowWarning(false);
    
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    
    // Set new warning timeout
    warningTimeoutRef.current = setTimeout(() => {

      setShowWarning(true);
      // Immediately auto-save a draft on warning using the last known form data
      try {
        if (lastFormDataRef.current) {
          formDraftService.autoSave(lastFormDataRef.current, lastStepRef.current || 0);
        }
      } catch {}
    }, (timeoutDuration - warningTime) * 1000);
    
    // Set new session timeout
    timeoutRef.current = setTimeout(() => {

      handleSessionTimeout();
    }, timeoutDuration * 1000);
  }, [timeoutDuration, warningTime]);

  // Handle session timeout
  const handleSessionTimeout = useCallback(() => {
    setIsActive(false);
    setShowWarning(false);
    // Final safeguard: persist a draft right before logout
    try {
      if (lastFormDataRef.current) {
        formDraftService.saveDraftLocally(lastFormDataRef.current, lastStepRef.current || 0);
      }
    } catch {}
    logout();
  }, [logout]);

  // Extend session
  const extendSession = useCallback(() => {
    resetSession();
  }, [resetSession]);

  // Save and logout
  const saveAndLogout = useCallback((formData?: any, step?: number) => {
    if (formData) {
      formDraftService.saveDraftLocally(formData, step || 0);
    }
    setShowWarning(false);
    logout();
  }, [logout]);

  // Logout without saving
  const forceLogout = useCallback(() => {
    setShowWarning(false);
    logout();
  }, [logout]);

  // Auto-save functionality
  const startAutoSave = useCallback((formData: any, step: number = 0) => {
    // Track the latest form data and step for emergency saves (warning/timeout)
    lastFormDataRef.current = formData;
    lastStepRef.current = step;
    if (autoSaveTimeoutRef.current) {
      clearInterval(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setInterval(() => {
      if (isActive) {
        // Refresh latest refs in case callers pass new objects over time
        lastFormDataRef.current = formData;
        lastStepRef.current = step;
        formDraftService.autoSave(formData, step);
      }
    }, autoSaveInterval * 1000);
  }, [autoSaveInterval, isActive]);

  // Stop auto-save
  const stopAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearInterval(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  // Check for user activity
  useEffect(() => {
    const handleActivity = (event: Event) => {
      if (isActive) {
        const now = Date.now();
        const shouldLog = now - lastActivityLogTime.current > 2000; // Log at most once every 2 seconds
        const isSignificantEvent = ['mousedown', 'click', 'keydown', 'scroll', 'touchstart'].includes(event.type);
        
        if (shouldLog && isSignificantEvent) {
          lastActivityLogTime.current = now;
        }
        // Always reset session on any activity - this ensures stability
        resetSession();
      }
    };

    // Listen for user activity - but avoid interfering with form inputs
    const events = [
      'mousedown', 'mousemove', 'scroll', 'touchstart', 'click', 'keydown'
    ];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, false);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, false);
      });
    };
  }, [isActive, resetSession]);

  // Proactive background token refresh while user is active
  const scheduleProactiveRefresh = useCallback(() => {
    // Clear any existing scheduled refresh
    if (proactiveRefreshTimeoutRef.current) {
      window.clearTimeout(proactiveRefreshTimeoutRef.current);
      proactiveRefreshTimeoutRef.current = null;
    }

    try {
      if (!isActive) return;
      const token = getToken();
      if (!token) return;
      const decoded: any = jwtDecode(token);
      const expMs = (decoded?.exp ? decoded.exp * 1000 : 0);
      if (!expMs) return;
      const now = Date.now();
      const bufferMs = 30_000; // refresh 30s before expiry
      const delay = expMs - now - bufferMs;
      if (delay <= 0) {
        // Already close to expiry; refresh immediately but throttle attempts
        if (now - lastRefreshAttempt.current < 5000) return;
        lastRefreshAttempt.current = now;
        const currentRefresh = localStorage.getItem('refresh_token');
        if (!currentRefresh) return;
        backendRefreshToken(currentRefresh)
          .then((data: any) => {
            const newAccess = data.access_token || data.accessToken || data.token;
            const newRefresh = data.refresh_token || data.refreshToken;
            if (newAccess) {
              localStorage.setItem('access_token', newAccess);
              api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
            }
            if (newRefresh) {
              localStorage.setItem('refresh_token', newRefresh);
            }
            resetSession();
            // Schedule next based on new token
            scheduleProactiveRefresh();
          })
          .catch(() => {
            // swallow; ProtectedRoute/axios will handle hard failures
          });
        return;
      }

      proactiveRefreshTimeoutRef.current = window.setTimeout(() => {
        const currentRefresh = localStorage.getItem('refresh_token');
        if (!currentRefresh) return;
        lastRefreshAttempt.current = Date.now();
        backendRefreshToken(currentRefresh)
          .then((data: any) => {
            const newAccess = data.access_token || data.accessToken || data.token;
            const newRefresh = data.refresh_token || data.refreshToken;
            if (newAccess) {
              localStorage.setItem('access_token', newAccess);
              api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
            }
            if (newRefresh) {
              localStorage.setItem('refresh_token', newRefresh);
            }
            resetSession();
            scheduleProactiveRefresh();
          })
          .catch(() => {
            // If it fails, do not reschedule; guards will handle on demand
          });
      }, delay);
    } catch {}
  }, [isActive, resetSession]);

  useEffect(() => {
    scheduleProactiveRefresh();
    return () => {
      if (proactiveRefreshTimeoutRef.current) {
        window.clearTimeout(proactiveRefreshTimeoutRef.current);
        proactiveRefreshTimeoutRef.current = null;
      }
    };
  }, [scheduleProactiveRefresh]);

  // Initialize session on mount
  useEffect(() => {

    resetSession();
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (autoSaveTimeoutRef.current) clearInterval(autoSaveTimeoutRef.current);
    };
  }, [resetSession, timeoutDuration]);

  // Update time left for warning display
  useEffect(() => {
    if (showWarning && timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSessionTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [showWarning, timeLeft, handleSessionTimeout]);

  return {
    showWarning,
    timeLeft,
    isActive,
    lastActivity,
    extendSession,
    saveAndLogout,
    forceLogout,
    startAutoSave,
    stopAutoSave,
    resetSession
  };
};



